import { style, types } from 'typestyle';

export namespace CommonStyles {
  export namespace Colors {
    export const White: string = 'rgb(255,255,255)';
    export const Black: string = 'rgb(0,0,0)';
    export const Grey51: string = 'rgb(51,51,51)'; // #333333
    export const Grey79: string = 'rgb(79,79,79)'; //#4F4F4F
    export const Grey102: string = 'rgb(102,102,102)'; // #666666
    export const Grey153: string = 'rgb(153,153,153)'; // #999999
    export const Grey178: string = 'rgb(178,178,178)';
    export const Grey203: string = 'rgb(203,203,203)'; //  #CBCBCB
    export const Grey218: string = 'rgb(218,218,218)';
    export const Grey230: string = 'rgb(230,230,230)'; //  #E6E6E6
    export const Grey235: string = 'rgb(235,235,235)'; //#EBEBEB
    export const Grey245: string = 'rgb(245,245,245)';
    export const Grey250: string = 'rgb(250,250,250)'; //#FAFAFA
    export const HyperlinkBlue: string = 'rgb(26,105,158)'; // #1A699E
    export const FocusBorderBlue = '#3498DB';
    export const BackgroundButtonBlue = '#E0EFFB';
    export const BadgeBlue: string = 'rgb(18,116,202)'; // #1274CA
    export const PressButtonBlue = '#BEDBF4';
    export const HoverButtonBlue = '#D5E9F9';
    export const FontButtonBlue = '#2F6282';
    export const NormalTextColor: string = Colors.Grey102;
    export const DividerLineColor: string = Colors.Grey230;
  }

  export namespace Sizes {
    export const SmallFontSize: number = 12;
    export const NormalFontSize: number = 14;
    export const LargeFontSize: number = 18;
    export const HeaderFontSize: number = 24;
    export const NormalIconSize: number = 19;
    export const NormalIconBackgroundSize: string = NormalIconSize + 'px ' + NormalIconSize + 'px';
    export const DropdownItemHeight: number = 30;
    export const CompactRowHeight: number = 27;
  }

  export namespace Spacing {
    export const SmallSpace: number = 3;
    export const DefaultSpace: number = 6;
    export const MediumSpace: number = 12;
    export const LargeSpace: number = 18;
  }

  // The following code is copied from VPC.
  export namespace Layout {
    export const FlexBlockLeftAndCenter: React.CSSProperties = {
      display: 'flex',
      justifyContent: 'flex-start',
      alignItems: 'center',
    };

    export const FlexBlockVerticalCenter: React.CSSProperties = {
      display: 'flex',
      alignItems: 'center',
    };

    export const FlexInlineBlockLeftAndCenter: React.CSSProperties = {
      ...FlexBlockLeftAndCenter,
      display: 'inline-flex',
    };

    export const FlexVerticalAndHorizontalCenter: React.CSSProperties = {
      ...FlexBlockVerticalCenter,
      width: '100%',
      height: '100%',
      justifyContent: 'center',
    };

    export const FlexRight: React.CSSProperties = {
      display: 'flex',
      flexDirection: 'row',
      justifyContent: 'flex-end',
    };

    export const TextEllipsis: React.CSSProperties = {
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    };

    export const TextAlignCenter: React.CSSProperties = {
      textAlign: 'center',
    };

    export const TextAlignRight: React.CSSProperties = {
      textAlign: 'right',
    };
  }

  export namespace Typography {
    export const FontFamily: string = `'Benton Sans', 'Arial', 'Helvetica', 'sans-serif'`;

    export const defaultFont = {
      fontFamily: `${FontFamily}`,
      fontSize: Sizes.NormalFontSize + 'px',
      lineHeight: '16px',
      color: Colors.Grey51,
    };

    export const titleFont = {
      fontSize: Sizes.HeaderFontSize + 'px',
      fontFamily: `${FontFamily}`,
      color: Colors.Grey51,
    };
  }
  export namespace Button {
    export const baseButtonStyle = {
      ...CommonStyles.Typography.defaultFont,
      color: Colors.Grey51,
      borderRadius: '10px',
      padding: '3px',
      outline: 'none',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      height: '32px',
    };

    export const blueButtonStyle = {
      ...baseButtonStyle,
      color: Colors.FontButtonBlue,
      fontWeight: 'bold',
    };

    export const defaultButton = {
      'button:not(:focus)': {
        border: '1px solid Colors.Grey203',
      },
      'button:hover:enabled:not(:focus)': {
        borderColor: Colors.Grey203,
        backgroundColor: Colors.Grey250,
      },
    };

    export const blueButton = {
      'button:not(:focus)': {
        border: '1px solid transparent',
        backgroundColor: Colors.BackgroundButtonBlue,
      },
      'button:hover:enabled:not(:focus)': {
        border: '1px solid transparent',
        backgroundColor: Colors.HoverButtonBlue,
      },
      '.outline.outline:active:enabled': {
        border: '1px solid transparent',
        backgroundColor: Colors.PressButtonBlue,
      },
      'button:focus': {
        backgroundColor: Colors.BackgroundButtonBlue,
      },
    };
  }

  // copied from VPC
  export namespace Focus {
    export const IndicatorWidth: number = 1;

    export const Indicator: React.CSSProperties = {
      border: `${IndicatorWidth}px solid ${CommonStyles.Colors.FocusBorderBlue}`,
      boxShadow: `0 0 4px 0 ${CommonStyles.Colors.FocusBorderBlue}`,
    };
  }
}

export type Style = types.NestedCSSProperties;

/** Creates a unique classname for the specified styles using TypeStyle */
export function cssClass(name: string, ...styles: Style[]): string {
  return style(...styles, {
    $debugName: `${name}`,
    $unique: true,
  });
}

export const boxStyle: Style = {
  display: 'inline-block',
  verticalAlign: 'top',
};

// TBD convert the following 3 styles to standard Layout styles.
export const fillStyle: Style = {
  position: 'absolute',
  left: 0,
  right: 0,
  top: 0,
  bottom: 0,
};

export const flexColumnStyle: Style = {
  display: 'flex',
  flexDirection: 'column',
};

export const flexRowStyle: Style = {
  display: 'flex',
  flexDirection: 'row',
};

export const textEllipsis: Style = {
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};
